import React from 'react';
import { Wifi, WifiOff, AlertTriangle, RefreshCw } from 'lucide-react';
import { useNetworkResilience } from '../hooks/useNetworkResilience';

/**
 * COMPONENT: NetworkStatusIndicator
 *
 * Provides real-time visual feedback about network status:
 * - Online/Offline status
 * - Connection quality (slow connection warning)
 * - Pending requests counter
 * - Retry button for failed requests
 *
 * Fixes: User confusion about connection state, invisible loading states
 */

export const NetworkStatusIndicator: React.FC = () => {
  const { networkStatus, isPending, pendingCount, clearQueue } = useNetworkResilience();

  // Don't show anything if everything is fine
  if (networkStatus.isOnline && !networkStatus.isSlowConnection && !isPending) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-2">
      {/* Offline Indicator */}
      {!networkStatus.isOnline && (
        <div className="bg-red-900/90 backdrop-blur-sm text-white px-4 py-3 rounded-lg shadow-lg border border-red-700 flex items-center space-x-3 animate-in slide-in-from-bottom">
          <WifiOff className="h-5 w-5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-sm">No Connection</p>
            <p className="text-xs text-red-200 mt-0.5">You're offline. Changes will sync when online.</p>
          </div>
          {pendingCount > 0 && (
            <div className="bg-red-800 text-white px-2 py-1 rounded text-xs font-medium">
              {pendingCount}
            </div>
          )}
        </div>
      )}

      {/* Slow Connection Warning */}
      {networkStatus.isOnline && networkStatus.isSlowConnection && (
        <div className="bg-yellow-900/90 backdrop-blur-sm text-white px-4 py-3 rounded-lg shadow-lg border border-yellow-700 flex items-center space-x-3 animate-in slide-in-from-bottom">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-sm">Slow Connection</p>
            <p className="text-xs text-yellow-200 mt-0.5">
              Loading may take longer ({networkStatus.effectiveType})
            </p>
          </div>
        </div>
      )}

      {/* Pending Requests Indicator */}
      {isPending && pendingCount > 0 && networkStatus.isOnline && (
        <div className="bg-blue-900/90 backdrop-blur-sm text-white px-4 py-3 rounded-lg shadow-lg border border-blue-700 flex items-center space-x-3 animate-in slide-in-from-bottom">
          <RefreshCw className="h-5 w-5 flex-shrink-0 animate-spin" />
          <div className="flex-1">
            <p className="font-medium text-sm">Syncing</p>
            <p className="text-xs text-blue-200 mt-0.5">
              {pendingCount} request{pendingCount !== 1 ? 's' : ''} pending
            </p>
          </div>
          <button
            onClick={clearQueue}
            className="text-xs bg-blue-800 hover:bg-blue-700 px-2 py-1 rounded transition-colors"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};
